import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { appointmentService } from '../services/appointmentService';
import { patientService } from '../services/patientService';
import { practiceExtrasService } from '../services/practiceExtrasService';

const emptyForm = {
  title: '',
  patientId: '',
  type: 'therapy',
  startAt: '',
  endAt: '',
  location: '',
  isTelehealth: false,
  telehealthUrl: '',
  notes: '',
  status: 'scheduled',
};

const Appointments = () => {
  const navigate = useNavigate();
  const [appointments, setAppointments] = useState([]);
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      setError('');
      const [apptRes, patientRes] = await Promise.all([
        appointmentService.list(),
        patientService.getPatients({}),
      ]);
      setAppointments(apptRes.data || []);
      setPatients(Array.isArray(patientRes) ? patientRes : patientRes.data || []);
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Failed to load appointments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.patientId || !form.title || !form.startAt || !form.endAt) {
      alert('Title, patient, start, and end are required');
      return;
    }
    try {
      setSaving(true);
      await appointmentService.create({
        ...form,
        startAt: new Date(form.startAt).toISOString(),
        endAt: new Date(form.endAt).toISOString(),
      });
      setForm(emptyForm);
      setShowForm(false);
      await load();
    } catch (err) {
      alert(err.response?.data?.message || err.message || 'Failed to create appointment');
    } finally {
      setSaving(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      const res = await appointmentService.update(id, { status });
      if (status === 'completed' && res.billingDraft) {
        alert('Visit completed. A billing draft was created for clinician review (not submitted).');
      } else if (status === 'completed') {
        alert('Visit marked completed. Check Billing for an auto-draft if one was created.');
      }
      await load();
    } catch (err) {
      alert(err.response?.data?.message || err.message);
    }
  };

  const formatWhen = (start, end) => {
    const s = new Date(start);
    const e = new Date(end);
    return `${s.toLocaleString()} – ${e.toLocaleTimeString()}`;
  };

  return (
    <Layout>
      <div className="p-6 md:p-8">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div>
              <h1 className="text-3xl font-poppins font-semibold text-primary">Appointments</h1>
              <p className="text-gray-500 mt-1">Provider calendar for therapy, assessment, and follow-up visits</p>
            </div>
            <button
              onClick={() => setShowForm((v) => !v)}
              className="px-4 py-2 bg-primary text-white rounded-lg font-medium"
            >
              {showForm ? 'Cancel' : '+ Schedule visit'}
            </button>
          </div>

          {showForm && (
            <form onSubmit={handleCreate} className="bg-white border border-gray-200 rounded-xl p-5 mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-gray-600">Title</label>
                <input name="title" value={form.title} onChange={handleChange} className="w-full mt-1 border rounded-lg px-3 py-2" placeholder="e.g. Therapy session" />
              </div>
              <div>
                <label className="text-sm text-gray-600">Patient</label>
                <select name="patientId" value={form.patientId} onChange={handleChange} className="w-full mt-1 border rounded-lg px-3 py-2">
                  <option value="">Select patient</option>
                  {patients.map((p) => (
                    <option key={p._id} value={p._id}>{p.fullName}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm text-gray-600">Type</label>
                <select name="type" value={form.type} onChange={handleChange} className="w-full mt-1 border rounded-lg px-3 py-2">
                  <option value="intake">Intake</option>
                  <option value="therapy">Therapy</option>
                  <option value="assessment">Assessment</option>
                  <option value="feedback">Feedback</option>
                  <option value="follow_up">Follow-up</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="text-sm text-gray-600">Location</label>
                <input name="location" value={form.location} onChange={handleChange} className="w-full mt-1 border rounded-lg px-3 py-2" />
              </div>
              <div>
                <label className="text-sm text-gray-600">Start</label>
                <input type="datetime-local" name="startAt" value={form.startAt} onChange={handleChange} className="w-full mt-1 border rounded-lg px-3 py-2" />
              </div>
              <div>
                <label className="text-sm text-gray-600">End</label>
                <input type="datetime-local" name="endAt" value={form.endAt} onChange={handleChange} className="w-full mt-1 border rounded-lg px-3 py-2" />
              </div>
              <div className="md:col-span-2 flex items-center gap-2">
                <input type="checkbox" name="isTelehealth" checked={form.isTelehealth} onChange={handleChange} id="telehealth" />
                <label htmlFor="telehealth" className="text-sm text-gray-700">Telehealth visit</label>
              </div>
              {form.isTelehealth && (
                <div className="md:col-span-2">
                  <label className="text-sm text-gray-600">Telehealth link</label>
                  <input
                    name="telehealthUrl"
                    value={form.telehealthUrl}
                    onChange={handleChange}
                    className="w-full mt-1 border rounded-lg px-3 py-2"
                    placeholder="https://…"
                  />
                </div>
              )}
              <div className="md:col-span-2">
                <label className="text-sm text-gray-600">Notes</label>
                <textarea name="notes" value={form.notes} onChange={handleChange} className="w-full mt-1 border rounded-lg px-3 py-2" rows={2} />
              </div>
              <div className="md:col-span-2">
                <button type="submit" disabled={saving} className="px-5 py-2 bg-primary text-white rounded-lg disabled:opacity-60">
                  {saving ? 'Saving…' : 'Create appointment'}
                </button>
              </div>
            </form>
          )}

          {error && <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg">{error}</div>}

          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            {loading ? (
              <div className="p-10 text-center text-gray-500">Loading appointments…</div>
            ) : appointments.length === 0 ? (
              <div className="p-10 text-center text-gray-500">No appointments yet. Schedule your first visit.</div>
            ) : (
              <table className="w-full text-left">
                <thead className="bg-gray-50 text-sm text-gray-600">
                  <tr>
                    <th className="px-4 py-3">When</th>
                    <th className="px-4 py-3">Patient</th>
                    <th className="px-4 py-3">Type</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {appointments.map((a) => (
                    <tr key={a._id} className="border-t border-gray-100">
                      <td className="px-4 py-3 text-sm">{formatWhen(a.startAt, a.endAt)}</td>
                      <td className="px-4 py-3 text-sm font-medium">{a.patientId?.fullName || '—'}</td>
                      <td className="px-4 py-3 text-sm capitalize">{a.type?.replace('_', ' ')}</td>
                      <td className="px-4 py-3 text-sm capitalize">{a.status?.replace('_', ' ')}</td>
                      <td className="px-4 py-3 text-sm space-x-2">
                        {a.status !== 'completed' && (
                          <button onClick={() => updateStatus(a._id, 'completed')} className="text-primary hover:underline">Complete</button>
                        )}
                        {a.status === 'completed' && a.patientId && (
                          <>
                            <button
                              type="button"
                              className="text-primary hover:underline"
                              onClick={() =>
                                navigate(
                                  `/progress-notes?patientId=${a.patientId._id || a.patientId}&appointmentId=${a._id}`,
                                )
                              }
                            >
                              Write note
                            </button>
                            <button
                              type="button"
                              className="text-primary hover:underline"
                              onClick={async () => {
                                try {
                                  const amount = window.prompt('Invoice amount (dollars):', '150');
                                  if (amount == null) return;
                                  await practiceExtrasService.createInvoice({
                                    patientId: a.patientId._id || a.patientId,
                                    appointmentId: a._id,
                                    amountCents: Math.round(parseFloat(amount) * 100),
                                    description: a.title || `${a.type} visit`,
                                    status: 'draft',
                                  });
                                  alert('Invoice draft created. Opening Billing…');
                                  navigate('/billing?tab=invoices');
                                } catch (e) {
                                  alert(e.response?.data?.message || e.message);
                                }
                              }}
                            >
                              Create invoice
                            </button>
                          </>
                        )}
                        {a.status !== 'cancelled' && (
                          <button onClick={() => updateStatus(a._id, 'cancelled')} className="text-red-600 hover:underline">Cancel</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Appointments;
